/**
 * Response component with citation hover support
 * Uses Streamdown's component override to handle citations
 * RFC-070: Citation hover previews
 */

import { useMemo, useEffect, useRef, useState } from 'react';
import { Streamdown } from 'streamdown';
import { cn } from '@/lib/utils';
import { chatStreamdownComponents } from './ChatLink';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
} from '@floating-ui/react';
import { SourceTypeIcon } from '@/utils/file-icons';

interface Source {
  id: string;
  title: string;
  slug?: string;
  sourceType?: string;
  publishedUrl?: string | null;
  topicId?: string;
  docId?: string;
  content?: string;
  score?: number;
  citationNumber?: number;
}

interface ResponseWithCitationsProps {
  content: string;
  sources?: Source[];
  className?: string;
}

export function ResponseWithCitations({ content, sources = [], className }: ResponseWithCitationsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredCitation, setHoveredCitation] = useState<{
    number: number;
    source?: Source;
  } | null>(null);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const { refs, floatingStyles } = useFloating({
    open: isPopoverOpen,
    onOpenChange: (open) => {
      setIsPopoverOpen(open);
      if (!open) {
        setHoveredCitation(null);
      }
    },
    placement: 'bottom',
    middleware: [offset(4), flip({ fallbackPlacements: ['top'] }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const sanitizedContent = useMemo(
    () => content.replace(/\n*Sources:\s*[\s\S]*$/g, '').replace(/\*\*\s*$/g, '').trimEnd(),
    [content]
  );

  const processedContent = useMemo(() => {
    // RFC-070: Parse inline citations: [text](cite:N:source_id:title)
    // Wrap cited text and append badge for highlighting on hover
    let processed = sanitizedContent.replace(
      /\[([^\]]+)\]\(cite:(\d+):([^:]+):([^)]+)\)/g,
      (match, text, citationNum, sourceId, title) => {
        const decodedTitle = decodeURIComponent(title.replace(/%20/g, ' '));
        return `<span class="citation-wrapper" data-citation-wrapper="${citationNum}"><span class="cited-text transition-colors" data-cited-text="${citationNum}">${text}</span><span data-citation-id="${sourceId}" data-citation-num="${citationNum}" data-citation-title="${decodedTitle}" class="citation-badge inline-flex items-center justify-center cursor-help bg-muted hover:bg-primary text-muted-foreground hover:text-primary-foreground transition-colors rounded-sm px-1.5 py-0.5 text-[10px] font-medium ml-1 align-middle">${citationNum}</span></span>`;
      }
    );

    // Fallback: handle bare [cite:N:source_id:title] references
    processed = processed.replace(
      /\[cite:(\d+):([^:]+):([^\]]+)\]/g,
      (match, citationNum, sourceId, title) => {
        const decodedTitle = decodeURIComponent(title.replace(/%20/g, ' '));
        return `<span class="citation-wrapper" data-citation-wrapper="${citationNum}"><span data-citation-id="${sourceId}" data-citation-num="${citationNum}" data-citation-title="${decodedTitle}" class="citation-badge inline-flex items-center justify-center cursor-help bg-muted hover:bg-primary text-muted-foreground hover:text-primary-foreground transition-colors rounded-sm px-1.5 py-0.5 text-[10px] font-medium align-middle">${citationNum}</span></span>`;
      }
    );
    return processed;
  }, [sanitizedContent]);

  useEffect(() => {
    if (!containerRef.current) return;

    const cancelClose = () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
    };

    const scheduleClose = () => {
      cancelClose();
      closeTimeoutRef.current = setTimeout(() => {
        setIsPopoverOpen(false);
      }, 150);
    };

    const handleMouseEnter = (event: MouseEvent) => {
      const target = event.currentTarget as HTMLElement;
      const citationId = target.getAttribute('data-citation-id');
      const citationNum = target.getAttribute('data-citation-num');
      const citationTitle = target.getAttribute('data-citation-title');

      if (!citationId || !citationNum) return;

      cancelClose();

      const citationNumber = parseInt(citationNum, 10);
      // Find source by ID from sources array
      const source = sources.find((s) => s.id === citationId);

      // Highlight the cited text
      const citedText = containerRef.current?.querySelector(
        `[data-cited-text="${citationNum}"]`
      ) as HTMLElement;
      if (citedText) {
        citedText.classList.add('bg-primary/10');
      }

      refs.setReference(target);
      setHoveredCitation({ number: citationNumber, source });
      setIsPopoverOpen(!!source);
    };

    const handleMouseLeave = () => {
      // Remove highlight from all cited texts
      const allCitedTexts = containerRef.current?.querySelectorAll('[data-cited-text]');
      allCitedTexts?.forEach((el) => {
        el.classList.remove('bg-primary/10');
      });
      scheduleClose();
    };

    // Use a slight delay to ensure Streamdown has finished rendering
    const attachListeners = () => {
      const citations = Array.from(
        containerRef.current?.querySelectorAll<HTMLElement>('[data-citation-id]') || []
      );

      citations.forEach((citation) => {
        citation.addEventListener('mouseenter', handleMouseEnter);
        citation.addEventListener('mouseleave', handleMouseLeave);
      });

      return citations;
    };

    // Try to attach immediately
    let citations = attachListeners();

    // If no citations found, wait a bit and try again
    let timeoutId: NodeJS.Timeout | null = null;
    if (citations.length === 0) {
      timeoutId = setTimeout(() => {
        citations = attachListeners();
      }, 100);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      citations.forEach((citation) => {
        citation.removeEventListener('mouseenter', handleMouseEnter);
        citation.removeEventListener('mouseleave', handleMouseLeave);
      });
      cancelClose();
    };
  }, [processedContent, refs, sources]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  return (
    <>
      <div ref={containerRef}>
        <Streamdown
          className={cn(
            'screenata-response size-full [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [overflow-wrap:anywhere] [&_ul]:list-outside [&_ul]:pl-4 [&_ol]:list-outside [&_ol]:pl-4',
            className
          )}
          skipHtml={false}
          components={chatStreamdownComponents}
        >
          {processedContent}
        </Streamdown>
      </div>

      {isPopoverOpen && hoveredCitation?.source && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className='z-[100] w-80 rounded-md border border-border bg-popover p-3 text-left shadow-lg transition-opacity duration-150 opacity-100'
            role='tooltip'
            onMouseEnter={() => {
              if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
                closeTimeoutRef.current = null;
              }
            }}
            onMouseLeave={() => {
              // Remove highlight from all cited texts when leaving popover
              const allCitedTexts = containerRef.current?.querySelectorAll('[data-cited-text]');
              allCitedTexts?.forEach((el) => {
                el.classList.remove('bg-primary/10');
              });

              if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
              }
              closeTimeoutRef.current = setTimeout(() => setIsPopoverOpen(false), 100);
            }}
          >
            <div className='space-y-1.5'>
              <div className='flex items-center gap-2'>
                <SourceTypeIcon
                  fileName={hoveredCitation.source.title || ''}
                  type={hoveredCitation.source.sourceType || 'File'}
                />
                <h4 className='source-type text-sm font-semibold leading-5'>
                  {hoveredCitation.source.title}
                </h4>
              </div>

              {hoveredCitation.source.content && (
                <div className='text-xs text-muted-foreground'>
                  <p className='line-clamp-2 leading-relaxed'>
                    {hoveredCitation.source.content
                      .replace(/^#+\s+/gm, '') // Remove markdown headers
                      .replace(/\*\*(.+?)\*\*/g, '$1') // Remove bold
                      .replace(/\*(.+?)\*/g, '$1') // Remove italic
                      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links, keep text
                      .replace(/`([^`]+)`/g, '$1') // Remove code formatting
                      .replace(/\n+/g, ' ') // Replace newlines with spaces
                      .trim()
                      .slice(0, 500)}...
                  </p>
                </div>
              )}

              {/* <div className='flex flex-wrap items-center gap-2 text-xs text-muted-foreground'>
                {hoveredCitation.source.sourceType && (
                  <span className='bg-secondary rounded-sm px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-secondary-foreground'>
                    {hoveredCitation.source.sourceType}
                  </span>
                )}
                {hoveredCitation.source.slug && (
                  <span className='font-mono text-[10px] opacity-70'>
                    /{hoveredCitation.source.slug}
                  </span>
                )}
              </div> */}

              {hoveredCitation.source.publishedUrl && (
                <a
                  href={hoveredCitation.source.publishedUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='text-primary inline-flex items-center gap-1 text-xs font-medium hover:underline'
                >
                  View document
                  <svg
                    className='h-3 w-3'
                    fill='none'
                    stroke='currentColor'
                    viewBox='0 0 24 24'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                    />
                  </svg>
                </a>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
